"use client";

import React, { useMemo, useState } from "react";
import styles from "./pricing-sidebar.module.css";
import { FiCornerDownRight } from "react-icons/fi";
import { FaShieldAlt } from "react-icons/fa";
import { useGetProductPriceHistoryQuery, useGetPriceHistoryTrendQuery } from "../../../../../reducers/priceHistoryApi";
import { useGetSettingByKeyQuery } from "../../../../../reducers/settingsApi";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from "react-router";
import { isDev } from "../../../../../config/default";

const PricingSidebar = ({ selectedItemBrandId, selectedProductInfo }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("crawl");
  const [trendDays, setTrendDays] = useState(30);

  // console.log("selectedProductInfo", selectedProductInfo);
  const productId = selectedItemBrandId?._id || selectedProductInfo?._id || selectedProductInfo?.productId;

  // --- 1. API Fetches ---
  const { data: historyResponse, isLoading: isHistoryLoading, isFetching } = useGetProductPriceHistoryQuery(
    { productId },
    { skip: !productId }
  );

  const { data: trendResponse, isLoading: isTrendLoading, isFetching: isTrendFetching } = useGetPriceHistoryTrendQuery(
    { productId, days: trendDays },
    { skip: !productId || activeTab !== 'history' }
  );

  const { data: settingsResponse } = useGetSettingByKeyQuery('app.pricinghistory');
  const { data: ignoreSettingsRes } = useGetSettingByKeyQuery('app.setting.ignoreCompetitor');

  const ignoredCompetitors = useMemo(() => {
    return ignoreSettingsRes?.data?.value || [];
  }, [ignoreSettingsRes]);

  // --- 2. Top-Level useMemo Hooks ---

  const pricingVars = useMemo(() => {
    let value = settingsResponse?.data?.value;
    if (typeof value === 'string') {
      try { value = JSON.parse(value); } catch (e) { return {}; }
    }
    return value?.veriables || {};
  }, [settingsResponse]);

  const historyData = useMemo(() => {
    let data = historyResponse?.data?.data || [];
    return [...data]
      .filter(item => item.price !== undefined && item.price !== null)
      .sort((a, b) => a.price - b.price);
  }, [historyResponse]);

  const metrics = useMemo(() => {
    const breakEvenRaw = selectedProductInfo?.evenPrice || historyResponse?.data?.evenPrice || selectedProductInfo?.cost || historyData[0]?.cost || 0;
    const breakEven = Number(breakEvenRaw);

    const pivotIndex = historyData.findIndex(item => 
      item.price > breakEven && 
      item.seller !== "Sundial Home" && 
      !ignoredCompetitors.includes(item.seller || item.name)
    );
    const pivotItem = pivotIndex !== -1 ? historyData[pivotIndex] : null;

    const priceFloor = historyResponse?.data?.floorPrice || (pivotItem
      ? pivotItem.price * (1 - (pricingVars.priceFloorMargin || 2) / 100)
      : breakEven * (1 + (pricingVars.priceFloorMargin || 2) / 100));

    const calculations = { breakEven, priceFloor, pivotIndex, pivotItem, strategies: [] };

    if (pivotItem && pricingVars) {
      const p = pivotItem.price;
      let foundFirstSafe = false;

      const maxDiscountPrice = p - (pricingVars.maxDiscount || 0);

      let rawStrategies = [
        { name: `Aggressive (${pricingVars.aggressive}% Lower)`, price: p * (1 - (pricingVars.aggressive || 5) / 100), type: 'aggressive', icon: <FiCornerDownRight /> },
        { name: `Price Floor`, price: priceFloor, type: 'floor', icon: <FaShieldAlt /> },
        { name: `Conservative (${pricingVars.conservative}% Lower)`, price: p * (1 - (pricingVars.conservative || 1) / 100), type: 'conservative', icon: <FiCornerDownRight /> },
        { name: `Just Under ($${pricingVars.justBelow} Lower)`, price: p - (pricingVars.justBelow || 5), type: 'justUnder', icon: <FiCornerDownRight /> },
        { name: `Price Match`, price: p, type: 'match', icon: <FiCornerDownRight /> },
        { name: `Max Discount Limit`, price: maxDiscountPrice, type: 'maxDiscountLimit', icon: <FiCornerDownRight /> }
      ];

      rawStrategies.sort((a, b) => a.price - b.price);

      // Find the best safe strategy (discounted ones first, fallback to match)
      const discountedStrategies = rawStrategies.filter(s => ['aggressive', 'conservative', 'justUnder'].includes(s.type));
      let baseKeyPriceStrategy = discountedStrategies.find(s => s.price > priceFloor);

      let keyPriceStrategy = baseKeyPriceStrategy;

      // Apply Max Discount constraint: If our best discount is too deep, use the max allowed discount instead
      if (keyPriceStrategy && keyPriceStrategy.price < maxDiscountPrice && pricingVars.maxDiscount > 0) {
        keyPriceStrategy = rawStrategies.find(s => s.type === 'maxDiscountLimit');
      }

      if (!keyPriceStrategy) {
        keyPriceStrategy = rawStrategies.find(s => s.type === 'match');
      }

      calculations.strategies = rawStrategies.map(strat => {
        let bgColor = styles.bgBlue;
        let textColor = '#004085';
        let label = (strat.type === 'floor' || strat.type === 'match') ? '' : (strat.price < priceFloor ? 'Below Price Floor' : '');

        if (strat === keyPriceStrategy) {
          bgColor = styles.bgGreen;
          textColor = '#0d6832';
          label = 'Key Price';
        } else if (strat.type === 'floor') {
          bgColor = styles.bgYellow;
          textColor = '#856404';
        } else if (strat.price < priceFloor) {
          bgColor = styles.bgRed;
          textColor = '#801d4a';
        } else if (strat.price > priceFloor && !foundFirstSafe) {
          // This was the old logic for 'foundFirstSafe', but we want ONLY the Key Price to be green now.
          // To avoid disturbing other logic, we keep the flag but use blue for other safe strategies.
          bgColor = styles.bgBlue;
          textColor = '#004085';
          foundFirstSafe = true;
        }

        return { ...strat, bgColor, textColor, label: <span style={{ color: textColor }}>{label}</span> };
      });
    }
    return calculations;
  }, [historyData, pricingVars, selectedProductInfo, historyResponse]);

  const displayKeyPrice = useMemo(() => {
    const p = metrics.pivotItem?.price;
    const maxDiscountPrice = p ? p - (pricingVars.maxDiscount || 0) : null;

    // Follow the same logic: best discounted strategy > floor, capped by max discount, or fallback to match/original price.
    const discounted = metrics.strategies.filter(s => ['aggressive', 'conservative', 'justUnder'].includes(s.type));
    let bestSafe = discounted.find(s => s.price > metrics.priceFloor);

    if (bestSafe && maxDiscountPrice && bestSafe.price < maxDiscountPrice && pricingVars.maxDiscount > 0) {
      bestSafe = metrics.strategies.find(s => s.type === 'maxDiscountLimit');
    }

    const matchStrat = metrics.strategies.find(s => s.type === 'match');
    const keyPriceNode = bestSafe || matchStrat;

    return selectedProductInfo?.keyPrice || keyPriceNode?.price || historyData[0]?.price || 0.00;
  }, [metrics.strategies, historyData, selectedProductInfo, metrics.priceFloor, pricingVars.maxDiscount]);

  const marginMetrics = useMemo(() => {
    const dollar = displayKeyPrice - metrics.breakEven;
    const percent = displayKeyPrice > 0 ? (dollar / displayKeyPrice) * 100 : 0;
    return { dollar, percent };
  }, [displayKeyPrice, metrics.breakEven]);

  // --- 3. Price History Sorting Logic ---
  const trendData = useMemo(() => trendResponse?.data || [], [trendResponse]);

  const chartData = useMemo(() => {
    return trendData.map(dayData => {
      const dataPoint = { date: dayData.date };
      if (Array.isArray(dayData.sellers)) {
        dayData.sellers.forEach(s => { dataPoint[s.seller] = s.price; });
      }
      return dataPoint;
    });
  }, [trendData]);

  // Optimized sortedSellers to strictly follow Price ascending
  const sortedSellers = useMemo(() => {
    const sellerAggregates = {};
    trendData.forEach(dayData => {
      if (Array.isArray(dayData.sellers)) {
        dayData.sellers.forEach(s => {
          if (!sellerAggregates[s.seller]) sellerAggregates[s.seller] = [];
          sellerAggregates[s.seller].push(s.price);
        });
      }
    });

    return Object.keys(sellerAggregates)
      .map(name => ({
        name,
        // Using average price for stable sorting in the legend
        avg: sellerAggregates[name].reduce((a, b) => a + b, 0) / sellerAggregates[name].length
      }))
      .sort((a, b) => a.avg - b.avg) // Numeric Sort: Lowest Price Sellers First
      .map(item => item.name);
  }, [trendData]);

  const colorPalette = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c', '#d084d0', '#8dd1e1', '#ffb347', '#ba68c8', '#4db6ac'];

  // --- 4. Render Helpers ---
  const renderCrawlReport = () => {
    if (historyData.length === 0) {
      return (
        <div className={styles.reportList}>
          <div className="p-5 text-center text-muted">No crawl history available.</div>
        </div>
      );
    }

    return (
      <div className={styles.reportList}>
        {historyData.map((item, idx) => {
          const isBeforePivot = metrics.pivotIndex !== -1 && idx < metrics.pivotIndex;
          const isPivot = idx === metrics.pivotIndex;
          const isIgnored = ignoredCompetitors.includes(item.seller || item.name);

          if (isPivot) {
            return (
              <div key={`pivot-group-${idx}`} className={styles.nestedBox}>
                {metrics.strategies
                  .filter(strat => strat.type !== 'match' && (strat.type !== 'maxDiscountLimit' || strat.label?.props?.children === 'Key Price'))
                  .map((strat, sIdx) => (
                    <div key={`strat-${sIdx}`} className={`${styles.nestedRow} ${strat.bgColor}`}>
                      <div className={styles.nestedIcon} style={{ color: strat.textColor }}>{strat.icon}</div>
                      <div className={styles.rowBrand} style={{ color: strat.textColor }}>{strat.type === 'floor' ? 'Price Floor' : item.seller || "Competitor"}</div>
                      <div className={styles.rowStatus} style={{ color: strat.textColor }}>{strat.type === 'floor' ? '' : strat.name}</div>
                      <div className={styles.rowLabel}>{strat.label}</div>
                      <div className={styles.rowPrice} style={{ color: strat.textColor }}>${strat.price?.toFixed(2)}</div>
                    </div>
                  ))}
                {(() => {
                  const matchStrat = metrics.strategies.find(s => s.type === 'match');
                  const bgColor = matchStrat?.label?.props?.children === 'Key Price' ? styles.bgGreen : styles.bgBlue;
                  const textColor = matchStrat?.label?.props?.children === 'Key Price' ? '#0d6832' : '#004085';

                  return (
                    <div className={`${styles.nestedRow} ${bgColor}`}>
                      <div className={styles.rowIndex} style={{ color: textColor }}>{idx + 1}</div>
                      <div className={styles.rowBrand} style={{ color: textColor }}>{item.seller || "Competitor"}</div>
                      <div className={styles.rowStatus} style={{ color: textColor }}>Price Match</div>
                      <div className={styles.rowLabel}>{matchStrat?.label}</div>
                      <div className={styles.rowPrice} style={{ color: textColor }}>${item.price?.toFixed(2)}</div>
                    </div>
                  );
                })()}
              </div>
            );
          }

          let rowBg = isBeforePivot ? styles.bgPink : styles.bgGrey;
          let rowOpacity = 1;
          let statusLabel = "Price Match";

          if (isIgnored) {
            rowBg = styles.bgGrey;
            rowOpacity = 0.6;
            statusLabel = "Ignored";
          }

          return (
            <div 
              key={`row-${idx}`} 
              className={`${styles.reportRow} ${rowBg}`}
              style={{ opacity: rowOpacity }}
            >
              <div className={styles.rowIndex}>{idx + 1}</div>
              <div className={styles.rowBrand}>{item.seller || "Competitor"}</div>
              <div className={styles.rowStatus}>{statusLabel}</div>
              <div className={styles.rowLabel}>{isIgnored && <span style={{ color: '#888', fontSize: '11px' }}>Global Ignore List</span>}</div>
              <div className={styles.rowPrice}>${item.price?.toFixed(2)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // Memoized custom dot to prevent re-renders
  const CustomDot = useMemo(() => {
    return (props) => {
      const { cx, cy, stroke, dataKey } = props;
      // Deterministic shape based on seller index in sortedSellers
      const sellerIndex = sortedSellers.indexOf(dataKey);
      const isSquare = sellerIndex % 2 === 0;

      if (isSquare) {
        return <rect x={cx - 4} y={cy - 4} width={8} height={8} fill={stroke} />;
      }
      return <circle cx={cx} cy={cy} r={4.5} fill={stroke} />;
    };
  }, [sortedSellers]);

  const renderHistoryList = () => {
    // Group data by seller
    const sellerGroups = {};

    // Initialize groups based on sortedSellers to maintain order
    sortedSellers.forEach(seller => {
      sellerGroups[seller] = [];
    });

    trendData.forEach(day => {
      if (Array.isArray(day.sellers)) {
        day.sellers.forEach(s => {
          if (sellerGroups[s.seller]) {
            sellerGroups[s.seller].push({
              date: day.date,
              price: s.price
            });
          }
        });
      }
    });

    // Sort dates descending for each seller
    Object.keys(sellerGroups).forEach(seller => {
      sellerGroups[seller].sort((a, b) => new Date(b.date) - new Date(a.date));
    });

    // Filter out sellers with no data
    const activeSellers = sortedSellers.filter(s => sellerGroups[s] && sellerGroups[s].length > 0);

    if (activeSellers.length === 0) return null;

    return (
      <div className="mt-4 border-top pt-4">
        <h6 className="mb-3 font-weight-bold" style={{ color: '#333' }}>History Log</h6>
        <div className={styles.reportList} style={{ padding: 0 }}>
          {activeSellers.map((seller, sIdx) => {
            const sellerIndex = sortedSellers.indexOf(seller);
            const color = colorPalette[sellerIndex % colorPalette.length];
            const isSquare = sellerIndex % 2 === 0;

            return (
              <div key={seller} className="mb-4">
                <div className="d-flex align-items-center mb-2 px-1">
                  <span
                    style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: color,
                      borderRadius: isSquare ? '0' : '50%',
                      display: 'inline-block',
                      marginRight: '8px'
                    }}
                  />
                  <span className="font-weight-bold text-dark" style={{ fontSize: '14px' }}>
                     {seller} {ignoredCompetitors.includes(seller) && <span style={{ color: '#888', fontSize: '12px', fontWeight: 'normal', marginLeft: '6px' }}>(Ignored)</span>}
                   </span>
                </div>

                {sellerGroups[seller].map((item, idx) => (
                  <div key={`${seller}-${idx}`} className={`${styles.reportRow} ${styles.bgGrey}`} style={{ marginBottom: '4px', marginLeft: '24px', width: 'auto' }}>
                    <div className={styles.rowBrand} style={{ fontSize: '13px' }}>
                      {new Date(item.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                    </div>
                    <div className={styles.rowPrice}>${item.price?.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPriceHistory = () => {
    // Maintain consistent height for loading/empty states to avoid layout shifts
    const containerStyle = { backgroundColor: '#fff', minHeight: '400px', padding: '1rem' };

    return (
      <div style={containerStyle}>
        <div className="d-flex align-items-center mb-4">
          <select
            className={styles.periodSelect}
            value={trendDays}
            onChange={(e) => setTrendDays(Number(e.target.value))}
          >
            <option value={7}>Period: 7 days</option>
            <option value={30}>Period: 30 days</option>
            <option value={90}>Period: 90 days</option>
          </select>
        </div>

        {isTrendLoading || isTrendFetching ? (
          <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
            <div className="spinner-border spinner-border-sm text-primary"></div>
          </div>
        ) : trendData.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: '300px' }}>
            No history for this period.
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="0" vertical={true} horizontal={false} stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12, fill: '#000' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(val) => {
                    const d = new Date(val);
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                  padding={{ left: 20, right: 20 }} // Prevent dots from being cut off at edges
                  height={40}
                  dy={10}
                />
                <YAxis hide={true} domain={['auto', 'auto']} />

                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  formatter={(value) => `$${Number(value).toLocaleString()}`}
                  itemSorter={(item) => Number(item.value)}
                  cursor={{ stroke: '#ccc', strokeWidth: 1, strokeDasharray: '4 4' }}
                />

                {sortedSellers.map((name, idx) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={colorPalette[idx % colorPalette.length]}
                    strokeWidth={0} // Hide lines
                    connectNulls
                    dot={CustomDot}
                    activeDot={{ r: 6 }}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>

            {renderHistoryList()}
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.headerContainer}>
        <img src={selectedProductInfo?.productId?.mainImage || selectedProductInfo?.mainImage} alt="product" className={styles.headerImage} />
        <div className={styles.headerInfo}>
          <a
            href={
               isDev
                ? `https://qasundial.sundialhome.com/product/${selectedProductInfo?.slug}`
                : `https://www.sundialhome.com/product/${selectedProductInfo?.slug}`
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className={styles.productTitle}>{selectedProductInfo?.productName || selectedProductInfo?.name}</div></a>
          <div className={styles.brandName}>by {selectedProductInfo?.manufacturer || "Sunpan"}</div>
          <div className={styles.skuLink} onClick={() =>
            navigate(`/admin/products?sku=${selectedProductInfo?.sku || selectedProductInfo?.productId?.sku || ""}`)
          }>{selectedProductInfo?.sku || selectedProductInfo?.productId?.sku}</div>
          <div className={styles.crawlDate}>
            Crawl URL:
            <a
              href={historyResponse?.data?.sourceUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                marginLeft: "6px",
                padding: "4px 10px",
                fontSize: "12px",
                backgroundColor: "#2563eb",
                color: "#ffffff",
                borderRadius: "4px",
                textDecoration: "none",
                display: "inline-block",
                lineHeight: "1.4",
                cursor: "pointer"
              }}
            >
              Open Crawl
            </a>
          </div>

        </div>
        <div className={styles.metricsGrid}>
          <div>Break Even: <strong>${metrics.breakEven?.toFixed(2)}</strong></div>
          <div>Price Floor: <strong>${metrics.priceFloor?.toFixed(2)}</strong></div>
          <div style={{ color: '#0d6832' }}>Key Price: <strong>${displayKeyPrice?.toFixed(2)}</strong></div>
          <div>Margin $: <strong>${marginMetrics.dollar?.toFixed(2)}</strong></div>
          <div>Margin %: <strong>{marginMetrics.percent?.toFixed(2)}%</strong></div>
          <div>Algo Price %: <strong>${selectedProductInfo?.algoPrice}</strong></div>
        </div>
      </div>

      <div className={styles.tabHeader}>
        <div className={`${styles.tabItem} ${activeTab === 'crawl' ? styles.tabItemActive : ''}`} onClick={() => setActiveTab('crawl')}>Live Crawl</div>
        <div className={`${styles.tabItem} ${activeTab === 'history' ? styles.tabItemActive : ''}`} onClick={() => setActiveTab('history')}>Price History</div>
      </div>

      {activeTab === 'crawl' ? (isHistoryLoading || isFetching ? <div className="p-5 text-center"><div className="spinner-border spinner-border-sm text-primary"></div></div> : renderCrawlReport()) : renderPriceHistory()}
    </div>
  );
};

export default PricingSidebar;
